import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Search, Play, ExternalLink, TrendingUp, Users, Star } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ApifyActors() {
  const navigate = useNavigate();
  const [actors, setActors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedActor, setSelectedActor] = useState(null);
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [inputJson, setInputJson] = useState("{}");
  const [running, setRunning] = useState(false);

  useEffect(() => {
    fetchActors();
  }, []);

  const fetchActors = async (search = "") => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/api/admin/apify/actors`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { search: search || undefined, limit: 100 }
      });
      
      console.log("Apify actors response:", response.data);
      if (response.data.actors && response.data.actors.length > 0) {
        console.log("First actor sample:", response.data.actors[0]);
      }
      
      setActors(response.data.actors || []);
    } catch (error) {
      console.error("Error fetching actors:", error);
      if (error.response?.status === 400) {
        toast.error("Please configure Apify API key in Settings");
      } else {
        toast.error("Failed to fetch actors");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchActors(searchQuery);
  };

  const handleRunActor = (actor) => {
    setSelectedActor(actor);
    setInputJson("{}");
    setRunDialogOpen(true);
  };

  const handleConfirmRun = async () => {
    // Validate JSON
    try {
      JSON.parse(inputJson);
    } catch (e) {
      toast.error("Invalid JSON format");
      return;
    }

    setRunning(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/api/admin/apify/actors/${selectedActor.id}/run`,
        JSON.parse(inputJson),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Actor run started! Run ID: ${response.data.run_id}`);
      setRunDialogOpen(false);
    } catch (error) {
      console.error("Error running actor:", error);
      toast.error(error.response?.data?.detail || "Failed to run actor");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Apify Actors</h1>
          <p className="text-slate-600 mt-2">
            Browse and run 1,500+ web scraping actors
          </p>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <div className="flex-1">
                <Input
                  placeholder="Search actors (e.g., Instagram, Amazon, Google Maps)..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                />
              </div>
              <Button onClick={handleSearch} disabled={loading}>
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Actors List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : actors.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-slate-500">No actors found. Try a different search term.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {actors.map((actor) => (
              <Card 
                key={actor.id} 
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/data/apify-actors/${actor.id}`)}
              >
                <CardHeader>
                  <CardTitle className="text-lg line-clamp-1">{actor.title || actor.name}</CardTitle>
                  <CardDescription className="line-clamp-2">
                    {actor.description || "No description available"}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Users className="w-4 h-4" />
                    <span>by {actor.username}</span>
                  </div>

                  {actor.stats && (
                    <div className="flex gap-3 text-sm flex-wrap">
                      <div className="flex items-center gap-1 text-slate-600">
                        <TrendingUp className="w-4 h-4" />
                        <span>{actor.stats.totalRuns?.toLocaleString() || 0} runs</span>
                      </div>
                      {actor.stats.averageRating && (
                        <div className="flex items-center gap-1 text-amber-600">
                          <Star className="w-4 h-4 fill-amber-600" />
                          <span>
                            {actor.stats.averageRating.toFixed(1)}
                            {actor.stats.ratingsCount && (
                              <span className="text-slate-500 text-xs ml-1">
                                ({actor.stats.ratingsCount} {actor.stats.ratingsCount === 1 ? 'review' : 'reviews'})
                              </span>
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {actor.categories && actor.categories.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {actor.categories.slice(0, 3).map((cat) => (
                        <Badge key={cat} variant="secondary" className="text-xs">
                          {cat}
                        </Badge>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2 pt-2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRunActor(actor);
                      }}
                      size="sm"
                      className="flex-1"
                    >
                      <Play className="w-4 h-4 mr-1" />
                      Run
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      asChild
                    >
                      <a
                        href={`https://apify.com/${actor.username}/${actor.name}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Run Actor Dialog */}
        <Dialog open={runDialogOpen} onOpenChange={setRunDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Run Actor: {selectedActor?.title || selectedActor?.name}</DialogTitle>
              <DialogDescription>
                Configure the input for this actor. Provide JSON formatted data.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label>Input (JSON)</Label>
                <Textarea
                  value={inputJson}
                  onChange={(e) => setInputJson(e.target.value)}
                  placeholder='{\n  "startUrls": ["https://example.com"],\n  "maxItems": 10\n}'
                  className="font-mono text-sm min-h-[200px] mt-2"
                />
                <p className="text-xs text-slate-500 mt-2">
                  Check the{" "}
                  <a
                    href={`https://apify.com/${selectedActor?.username}/${selectedActor?.name}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    actor page
                  </a>
                  {" "}for input schema details.
                </p>
              </div>

              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setRunDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleConfirmRun} disabled={running}>
                  {running ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Starting...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      Run Actor
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
